import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import api from '../services/api';
import { FiUploadCloud, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const Upload = ({ onLogout }) => {
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;
  const isStudent = user?.role === 'student';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    department: user?.department || '',
    semester: '',
    type: 'note',
    year: '',
    examType: 'other',
  });

  const departments = ['Computer Science', 'Electronics', 'Mechanical', 'Civil', 'Electrical'];
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];
  const examTypes = ['midsem', 'endsem', 'quiz', 'other'];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'type' && e.target.value === 'pastpaper') {
      setFormData((prev) => ({ ...prev, type: 'pastpaper', examType: 'endsem' }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        e.target.value = "";
        setFile(null);
        return;
      }
      const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError("Only PDF, PNG, JPG files are allowed");
        e.target.value = "";
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!file) {
      setError('Please select a file to upload');
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append('file', file);
    Object.keys(formData).forEach(key => {
      if (formData[key]) data.append(key, formData[key]);
    });

    try {
      const endpoint = isStudent ? '/requests' : '/notes/upload';

      const response = await api.post(endpoint, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log('Upload response:', response);
      setSuccess(isStudent ? 'Request submitted successfully! A faculty member will review your note.' : 'Study material uploaded successfully!');

      // Reset form
      setFormData({
        title: '',
        description: '',
        subject: '',
        department: user?.department || '',
        semester: '',
        type: 'note',
        year: '',
        examType: 'other',
      });
      setFile(null);
      const input = document.getElementById('file-upload');
      if (input) input.value = '';

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar onLogout={onLogout} />

      <div className="main-content">
        <Header
          title={isStudent ? "Submit Note Request" : "Upload Study Material"}
          subtitle={isStudent ? "Submit study notes to your college teachers for review and publication." : "Add new notes or past papers directly to your institution's library."}
        />

        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="card">
            {error && (
              <div className="alert alert-error">
                <FiAlertCircle /> {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                <FiCheckCircle /> {success}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <h3 className="mb-3" style={{ fontSize: '16px', fontWeight: '600' }}>Resource Information</h3>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="e.g. Engineering Mechanics Unit 1 Notes"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="form-input"
                  placeholder="Brief summary of covered topics..."
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="e.g. Data Structures"
                  />
                </div>

                <div className="form-group">
                  <label>Department *</label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select Department</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Semester *</label>
                  <select
                    name="semester"
                    value={formData.semester}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select Semester</option>
                    {semesters.map(s => <option key={s} value={s}>Semester {s}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>Material Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="note">Note</option>
                    <option value="pastpaper">Past Paper</option>
                  </select>
                </div>
              </div>

              {formData.type === 'pastpaper' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label>Year</label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleChange}
                      min="2000"
                      max="2030"
                      className="form-input"
                      placeholder="e.g. 2024"
                    />
                  </div>
                  <div className="form-group">
                    <label>Exam Type</label>
                    <select
                      name="examType"
                      value={formData.examType}
                      onChange={handleChange}
                      className="form-input"
                    >
                      {examTypes.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <h3 className="mt-4 mb-3" style={{ fontSize: '16px', fontWeight: '600' }}>Document File</h3>

              <div className="form-group">
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="file-upload"
                    onChange={handleFileChange}
                    accept=".pdf,.png,.jpg,.jpeg"
                  />
                  <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'block' }}>
                    <FiUploadCloud size={44} style={{ color: 'var(--primary)', marginBottom: '8px' }} />
                    <p style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                      {file ? file.name : 'Click to select or drag document file'}
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {file ? `${(file.size / (1024 * 1024)).toFixed(2)} MB` : 'Supported formats: PDF, PNG, JPG (Maximum 10 MB)'}
                    </p>
                  </label>
                </div>
              </div>

              <div className="mt-4 d-flex justify-between align-center">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Uploading...' : (isStudent ? 'Submit Request' : 'Upload Resource')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upload;
